'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../../lib/utils';

/** The shadcn/ui Button, on `class-variance-authority` and `@radix-ui/react-slot`.
 *
 *  This file is the shadcn component itself — its anatomy, its `data-slot` attribute, its
 *  `variant`/`size` axes and `asChild` are shadcn's. What is ours is only the styling, which reads this
 *  app's design tokens instead of shadcn's default palette, plus two deliberate deviations noted below.
 *
 *  `components/ui/Button.tsx` and `components/ui/IconButton.tsx` are the app-shaped wrappers over this
 *  primitive and are what the app imports; this is the only place a button's geometry is declared. */

const buttonVariants = cva(
  // The border lives in the base rather than in the filled variants: every variant then reserves the
  // same 1px, so a ghost button and a filled one are exactly the same height and swapping between them
  // does not move the row. Variants only choose the border's COLOUR.
  // `box-shadow` joins the transition list because the `default` variant now rests on one; without it
  // the ring would snap while the fill faded, which is worse than either alone.
  //
  // The duration is `--motion-instant`, the app's own fastest step, rather than the reference's literal
  // 100ms: it resolves to 80ms and no skin retunes it. A press is the one interaction that has to feel
  // like it already happened, so 20ms under is the right side to miss on — and a token is what lets
  // `data-effects='off'` and prefers-reduced-motion zero it, which a literal would not.
  //
  // A disabled button must not answer the pointer. Each variant therefore pairs its `hover:` rules with
  // `disabled:hover:` ones that restore its resting fill, border and text. They are one step more specific
  // than any plain `hover:` a caller adds (`:disabled:hover` against `:hover`), so a disabled button keeps
  // its resting look whatever hover an override brings, while an enabled one and a link-shaped
  // `buttonClassName` anchor, which is never `:disabled`, still hover. `pointer-events-none` is not used:
  // the cursor has to stay `not-allowed`. The restores cover fill, border and text for every variant, not
  // only the properties the variant itself hovers, so a caller's hover on any of the three is covered. A
  // caller that overrides a variant's RESTING fill, border or text states its own `disabled:hover:`
  // restore with it; the merge then replaces the variant's.
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border text-ui ' +
    'transition-[color,background-color,border-color,box-shadow,transform] duration-[var(--motion-instant)] active:scale-[var(--press-scale)] ' +
    'pointer-coarse:min-h-[var(--touch-target)] ' +
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100 ' +
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // The one loud control on a page rests on `--shadow-primary`: a 1px brand ring and a short drop,
        // stated per design because a drop that reads on a near-white page is a no-op on a true-black
        // one. It is what makes the primary read as the ACTION rather than as a coloured label, and it
        // is a token rather than a baked shadow so a white-label theme retints it with `--primary-rgb`.
        default: 'border-primary bg-primary text-primary-foreground shadow-primary hover:bg-primary/90 disabled:hover:border-primary disabled:hover:bg-primary disabled:hover:text-primary-foreground',
        secondary: 'border-border bg-secondary text-secondary-foreground hover:border-border-strong hover:bg-secondary/80 disabled:hover:border-border disabled:hover:bg-secondary disabled:hover:text-secondary-foreground',
        destructive: 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:hover:border-destructive disabled:hover:bg-destructive disabled:hover:text-destructive-foreground',
        outline: 'border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
        // `accent` is the shadcn token for the interactive surface, and in this app it resolves to a wash
        // of the foreground rather than a step up the surface ramp. That matters: a surface step is a
        // highlight a design is free to erase, and both designs here do erase it, which is how a hover
        // ends up invisible. A wash of the text colour reads on every design by construction.
        ghost: 'border-transparent bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
        // shadcn's `link` variant is deliberately absent. It had no call site anywhere in the app, and as
        // a CVA cell it is not neutral: `link` × `icon` is a state nothing can render sensibly — an
        // underline-on-hover with no text to underline, in a square box — yet the gallery is exhaustive
        // over variant × size by construction, so the cell had to be either rendered or removed. A link
        // that looks like a link belongs in prose as an `<a>`, and a link that looks like a button is
        // already reachable through `asChild`.
        // Two app-specific variants, both with no shadcn counterpart, and both the same idea: a
        // destructive action that has to sit quietly in a row or a form. It stays unfilled until the
        // pointer is on it and only then admits what it does — a solid `destructive` button in every row
        // would shout the one thing the user is least likely to want.
        //
        // `ghost-destructive` is the ONE look of a destructive control that is not a labelled button: it
        // rests on the ghost convention (no edge, no fill, muted ink) and answers a pointer or keyboard
        // focus by going fully red — the solid `destructive` fill with its paired ink, the edge still
        // transparent. A faint wash was the earlier form, and it read as "slightly red" rather than as a
        // control that does something irreversible; `IconButton variant="danger"` is what a delete glyph
        // in a row or a toolbar takes, so the glyphs stopped being red-bordered squares.
        //
        // `outline-destructive` is the labelled toolbar button, where the action owns an edge and its name
        // is spelled out; it is red at rest on purpose, since it is never a lone glyph that could be
        // mistaken for decoration. `focus-visible` states the same fill as `hover` because the base draws
        // the keyboard halo but not the variant's own colour, and a red-on-red pair is the state a
        // keyboard user must see before a delete.
        'ghost-destructive': 'border-transparent bg-transparent text-muted-foreground hover:border-transparent hover:bg-destructive hover:text-destructive-foreground focus-visible:border-transparent focus-visible:bg-destructive focus-visible:text-destructive-foreground disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
        'outline-destructive': 'border-destructive bg-transparent text-destructive hover:bg-destructive hover:text-destructive-foreground disabled:hover:border-destructive disabled:hover:bg-transparent disabled:hover:text-destructive',
      },
      size: {
        // The height axis. The coarse floor is stated once in the base above, so every step keeps its
        // desktop height and still meets a finger's target; the two square steps add their width too.
        sm: 'h-8 gap-1.5 px-3',
        default: 'h-9 px-3.5',
        lg: 'h-10 px-5',
        icon: 'size-9 p-0 pointer-coarse:min-w-[var(--touch-target)]',
        // The compact square: one step below `icon`, for a compact action group inside a card or a list
        // row, where the group stays 32px as a whole instead of growing to the default 36px. It is square
        // like `icon` and takes the same coarse floor, so a finger gets 44px in every row.
        'icon-sm': 'size-8 p-0 pointer-coarse:min-w-[var(--touch-target)]',
        // The one-line REFERENCE step: a quote row or an inline jump that sits in running text without
        // pushing the line rhythm, so it is 20px tall with no padding or box of its own. A finger still gets
        // the 44px target because the HIT AREA is this step's own centred pseudo-element — the same idiom
        // `HelpTip` uses — which grows the target without moving the text. The coarse floor the other steps
        // take is declined here: it would make a reading row 44px tall inside the prose.
        inline: 'relative h-5 min-h-0 gap-1 px-0 py-0 text-small justify-start pointer-coarse:min-h-0 '
          + "before:absolute before:left-1/2 before:w-[calc(100%+--spacing(2))] before:min-w-[var(--touch-target)] before:content-[''] before:top-1/2 before:h-[var(--touch-target)] before:-translate-x-1/2 before:-translate-y-1/2",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className = '',
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';
  // `data-variant` and `data-size` are shadcn's own attributes: a container lays out a button by what it
  // is (the drawer's value column pulls a ghost button back by its padding) without a class of its own.
  return <Comp data-slot="button" data-variant={variant ?? 'default'} data-size={size ?? 'default'} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

export { Button, buttonVariants };
